import React, { useEffect } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { Card } from 'antd';

// Nodes
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { CodeNode, CodeHighlightNode } from '@lexical/code';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { 
    TableNode, 
    TableCellNode, 
    TableRowNode 
} from '@lexical/table';
import { MarkNode } from '@lexical/mark';
import { OverflowNode } from '@lexical/overflow';
import { ImageNode } from './nodes/ImageNode';

import './LexicalEditor.css';

const theme = {
    paragraph: 'editor-paragraph',
    heading: {
        h1: 'editor-heading-h1',
        h2: 'editor-heading-h2',
        h3: 'editor-heading-h3',
        h4: 'editor-heading-h4',
        h5: 'editor-heading-h5',
        h6: 'editor-heading-h6',
    },
    quote: 'editor-quote',
    list: {
        nested: {
            listitem: 'editor-nested-listitem',
        },
        ol: 'editor-list-ol',
        ul: 'editor-list-ul',
        listitem: 'editor-listitem',
    },
    link: 'editor-link',
    text: {
        bold: 'editor-text-bold',
        italic: 'editor-text-italic',
        underline: 'editor-text-underline',
        strikethrough: 'editor-text-strikethrough',
        underlineStrikethrough: 'editor-text-underlineStrikethrough',
        code: 'editor-text-code',
    },
    code: 'editor-code',
    table: 'editor-table',
    tableCell: 'editor-table-cell',
    tableCellHeader: 'editor-table-cell-header',
    mark: 'editor-mark',
    markOverlap: 'editor-mark-overlap',
};

// Plugin pour charger le contenu
function ContentLoaderPlugin({ content }) {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        if (content) {
            try {
                const editorState = editor.parseEditorState(content);
                editor.setEditorState(editorState);
            } catch (error) {
                console.error('Erreur lors du chargement du contenu:', error);
            }
        }
    }, [editor, content]);

    return null;
}

/**
 * Composant de visualisation de contenu Lexical en lecture seule
 * @param {Object} props
 * @param {string} props.content - Le contenu JSON Lexical à afficher
 * @param {boolean} props.showCard - Afficher dans une Card Ant Design (défaut: true)
 * @param {Object} props.cardStyle - Style personnalisé pour la Card
 * @param {string} props.className - Classe CSS personnalisée
 */
export default function LexicalViewer({ 
    content = null, 
    showCard = true,
    cardStyle = {},
    className = ''
}) {
    const initialConfig = {
        namespace: 'LexicalViewer',
        theme,
        onError: (error) => {
            console.error('Erreur Lexical Viewer:', error);
        },
        nodes: [
            HeadingNode,
            ListNode,
            ListItemNode,
            QuoteNode,
            CodeNode,
            CodeHighlightNode,
            TableNode,
            TableCellNode,
            TableRowNode,
            AutoLinkNode,
            LinkNode,
            OverflowNode,
            MarkNode,
            ImageNode
        ],
        editable: false, // Mode lecture seule
    };

    const viewerContent = (
        <div className={`editor-container viewer-mode ${className}`}>
            <LexicalComposer initialConfig={initialConfig}>
                <div className="editor-inner">
                    <RichTextPlugin
                        contentEditable={
                            <ContentEditable
                                className="editor-input viewer-content"
                                style={{
                                    minHeight: '100px',
                                    padding: '16px',
                                    outline: 'none',
                                    cursor: 'default'
                                }}
                            />
                        }
                        placeholder={null}
                        ErrorBoundary={LexicalErrorBoundary}
                    />
                    <ContentLoaderPlugin content={content} />
                </div>
            </LexicalComposer>
        </div>
    );

    if (showCard) {
        return (
            <Card
                bordered={false}
                style={{
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    ...cardStyle
                }}
                bodyStyle={{ padding: '24px' }}
            >
                {viewerContent}
            </Card>
        );
    }

    return viewerContent;
}
